import { Icon, Popover, Stack, Table, TableColumns, Tag, Text } from '@sixfold/common-ui';
import React, { forwardRef } from 'react';

import { CompanyRelationship } from '../../company/entities';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { CompanyRelationshipType, CompanySignupVisibilitySourceStatus } from '../../lib/graphql';
import {
  getSubcarrierSourceLabel,
  getVisibilitySourceDescription,
  getVisibilitySourceLabel,
  getVisibilitySourceStatusExplanation,
  getVisibilitySourceStatusLabel,
  getVisibilitySourceStatusSince,
  sortVisibilitySourcesByStatus,
  SUBCARRIERS_SOURCE_LABEL,
} from '../visibility_sources';

type SourceCompany = CompanyRelationship['sourceCompany'];
type VisibilitySource = NonNullable<SourceCompany['signupVisibilitySources']>[0];

type SourceDetailsRow = {
  id: string;
  source: string;
  description: string | null;
  status: VisibilitySource['status'];
  since: string | null;
};

const sourceDetailsColumns: TableColumns<SourceDetailsRow> = [
  {
    id: 'source',
    title: 'Source',
    width: '46%',
    cellContent: ({ value, row }) =>
      row.description === null ? (
        value
      ) : (
        <Stack direction="column">
          <span>{value}</span>
          <Text size="small" prominence="subtle">
            {row.description}
          </Text>
        </Stack>
      ),
  },
  {
    id: 'status',
    title: 'Status',
    width: '20%',
    cellContent: ({ value }) => getVisibilitySourceStatusLabel(value),
  },
  {
    id: 'since',
    title: 'Since',
    width: '34%',
    cellContent: ({ value }) => (value === null ? '-' : <FormattedDate date={value} />),
  },
];

const statusOrder: VisibilitySource['status'][] = [
  CompanySignupVisibilitySourceStatus.ACTIVE,
  CompanySignupVisibilitySourceStatus.IDENTIFIED,
  CompanySignupVisibilitySourceStatus.DEACTIVATED,
];

type SourceTagProps = Omit<React.ComponentProps<typeof Tag>, 'children'> & { label: string };

// Popover clones its child to attach the click handler and ref, so this must forward both to the Tag.
const SourceTag = forwardRef<HTMLDivElement, SourceTagProps>(function SourceTag({ label, ...rest }, ref) {
  return (
    <Tag size="small" ref={ref} className="company__relationships-visibilitySources-tag" {...rest}>
      {label}
    </Tag>
  );
});

export const RelationshipVisibilitySources: React.FC<{
  relationshipType: CompanyRelationship['relationshipType'];
  sourceCompany: SourceCompany;
}> = ({ relationshipType, sourceCompany }) => {
  const ownSources = sortVisibilitySourcesByStatus(sourceCompany.signupVisibilitySources ?? []);
  const viaSubcarriers = sortVisibilitySourcesByStatus(sourceCompany.signupVisibilitySourcesViaSubcarriers ?? []);
  const hasSubcarrierSources = viaSubcarriers.length > 0;

  if (relationshipType === CompanyRelationshipType.REFERRER || (ownSources.length === 0 && !hasSubcarrierSources)) {
    return <>-</>;
  }

  const events = sourceCompany.unifiedSignupEvents ?? [];
  const detailsRows: SourceDetailsRow[] = [
    ...ownSources.map((source) => ({
      id: source.type,
      source: getVisibilitySourceLabel(source.type),
      description: getVisibilitySourceDescription(source.type),
      status: source.status,
      since: getVisibilitySourceStatusSince(source, events),
    })),
    ...viaSubcarriers.map((source) => ({
      id: `subcarrier-${source.type}`,
      source: getSubcarrierSourceLabel(source.type),
      description: getVisibilitySourceDescription(source.type),
      status: source.status,
      since: null,
    })),
  ];

  const relevantStatusExplanations = statusOrder
    .filter((status) => detailsRows.some((row) => row.status === status))
    .map((status) => ({ status, explanation: getVisibilitySourceStatusExplanation(status) }));

  // Shared by every trigger (each tag and the info icon), so all of them open the same details.
  const detailsContent = (
    <Stack direction="column" gap="small">
      <Table data={detailsRows} columns={sourceDetailsColumns} />
      <Stack direction="column" gap="xsmall">
        {relevantStatusExplanations.map(({ status, explanation }) => (
          <Text size="small" prominence="subtle" key={status}>
            <Text as="span" size="small" prominence="subtle" weight="bold">
              {getVisibilitySourceStatusLabel(status)}:
            </Text>{' '}
            {explanation}
          </Text>
        ))}
      </Stack>
    </Stack>
  );

  return (
    <Stack
      className="company__relationships-visibilitySources"
      justifyContent="space-between"
      alignItems="start"
      wrap={false}
      gap="xsmall">
      <Stack className="company__relationships-visibilitySources-tags" alignItems="center" gap="xsmall">
        {/* A Tag only renders as a clickable button when it gets an onClick; Popover supplies that onClick. */}
        {ownSources.map((source) => (
          <Popover key={source.type} maxWidth={640} placement="bottom" content={detailsContent}>
            <SourceTag label={getVisibilitySourceLabel(source.type)} />
          </Popover>
        ))}
        {hasSubcarrierSources ? (
          <Popover maxWidth={640} placement="bottom" content={detailsContent}>
            <SourceTag label={SUBCARRIERS_SOURCE_LABEL} />
          </Popover>
        ) : null}
      </Stack>
      <Stack paddingBlockStart="xsmall">
        <Popover maxWidth={640} placement="bottom" content={detailsContent}>
          <span>
            <Icon name="small-info" size={16} />
          </span>
        </Popover>
      </Stack>
    </Stack>
  );
};
